import { transparentize } from "polished";
import React, { useState } from "react";
import { FaClock, FaLock } from "react-icons/fa";
import styled, { css, keyframes } from "styled-components";
import { space, SpaceProps } from "styled-system";
import { typeSize } from "../theme";

const SIZE = 20;
/** Length of the tick's path, for the draw-in animation. */
const TICK_LENGTH = 14;
/** A springy ease that overshoots a little, so the box "pops" back. */
const SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)";

const Label = styled.label<SpaceProps & { disabled?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
  font-weight: 500;
  font-size: ${typeSize("body")};
  color: ${(p) => p.theme.colors.text};
  -webkit-tap-highlight-color: transparent;
  ${space};
`;

Label.defaultProps = {
  mb: 2,
};

/** The real checkbox: invisible, but focusable and announced. */
const NativeInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: 0;
  padding: 0;
  opacity: 0;
  pointer-events: none;
`;

/** A ring that blooms out from the box and fades. */
const bloom = keyframes`
  from {
    opacity: 0.5;
    box-shadow: 0 0 0 0 currentColor;
  }
  to {
    opacity: 0;
    box-shadow: 0 0 0 9px currentColor;
  }
`;

type BoxProps = { checked: boolean; muted: boolean; bloom: boolean };

const Box = styled.span<BoxProps>`
  position: relative;
  flex: 0 0 ${SIZE}px;
  width: ${SIZE}px;
  height: ${SIZE}px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1.5px solid
    ${(p) => (p.checked ? p.theme.colors.primary : p.theme.colors.grey)};
  background-color: ${(p) =>
    p.checked ? p.theme.colors.primary : p.theme.colors.surface};
  color: ${(p) => p.theme.colors.textMuted};
  font-size: 10px;
  transition:
    background-color 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.35s ${SPRING};

  /* The ring that blooms out when someone ticks the box (not on load). */
  &::after {
    content: "";
    position: absolute;
    inset: -1.5px;
    border-radius: inherit;
    pointer-events: none;
    color: ${(p) => p.theme.colors.primary};
    opacity: 0;
    ${(p) =>
      p.bloom &&
      css`
        animation: ${bloom} 0.5s ease-out;
      `}
  }

  ${(p) =>
    p.muted &&
    css`
      background-color: ${p.theme.colors.sunken};
      border-color: ${p.theme.colors.border};
    `}

  /* Hover and press, only on boxes people can change. */
  label:hover > input:not(:disabled) + & {
    border-color: ${(p) =>
      p.checked ? p.theme.colors.primary : p.theme.colors.grey1};
  }

  label:active > input:not(:disabled) + & {
    transform: scale(0.86);
    transition-duration: 0.1s;
  }

  input:focus-visible + & {
    box-shadow: 0 0 0 3px
      ${(p) => transparentize(0.7, p.theme.colors.primary)};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::after {
      animation: none !important;
    }
  }
`;

/** The tick draws itself in when checked, and wipes out when unchecked. */
const Tick = styled.svg<{ checked: boolean }>`
  width: 14px;
  height: 14px;
  overflow: visible;

  path {
    fill: none;
    stroke: white;
    stroke-width: 2.4;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: ${TICK_LENGTH};
    stroke-dashoffset: ${(p) => (p.checked ? 0 : TICK_LENGTH)};
    transition: stroke-dashoffset
      ${(p) => (p.checked ? "0.22s ease-out 0.06s" : "0.1s ease-in")};
  }

  @media (prefers-reduced-motion: reduce) {
    path {
      transition: none;
    }
  }
`;

type CheckboxProps = SpaceProps & {
  value: boolean;
  label?: React.ReactNode;
  /** Leave out to draw a box only, inside a control that handles the click. */
  onChange?: (newValue: boolean) => void;
  name?: string;
  waiting?: boolean;
  disabled?: boolean;
  /** Accessible name when there's no visible label. */
  "aria-label"?: string;
};

const CheckBox = ({
  value,
  label,
  onChange,
  name,
  waiting,
  disabled,
  "aria-label": ariaLabel,
  ...rest
}: CheckboxProps) => {
  const [blooming, setBlooming] = useState(false);
  const interactive = onChange != null;
  const showTick = !disabled && !waiting;

  const box = (
    <Box
      checked={value && showTick}
      muted={!!disabled}
      bloom={blooming}
      onAnimationEnd={() => setBlooming(false)}
      aria-hidden
    >
      {disabled ? (
        <FaLock />
      ) : waiting ? (
        <FaClock />
      ) : (
        <Tick viewBox="0 0 14 14" checked={value}>
          <path d="M2.5 7.5l3 3 6-6.5" />
        </Tick>
      )}
    </Box>
  );

  // Presentational: the parent (e.g. a MultiSelect option) is the control.
  if (!interactive) {
    return (
      <Label as="span" {...rest}>
        {name != null && (
          <input type="hidden" name={name} value={value ? 1 : 0} />
        )}
        {box}
        {label}
      </Label>
    );
  }

  return (
    <Label {...rest} disabled={disabled}>
      {name != null && <input type="hidden" name={name} value={value ? 1 : 0} />}
      <NativeInput
        type="checkbox"
        checked={value}
        disabled={disabled}
        aria-label={label == null ? ariaLabel : undefined}
        aria-busy={waiting || undefined}
        onChange={(e) => {
          setBlooming(e.target.checked);
          onChange?.(e.target.checked);
        }}
        // The label's click already bubbles; don't let the click the
        // browser forwards to this input bubble a second time.
        onClick={(e) => e.stopPropagation()}
      />
      {box}
      {label}
    </Label>
  );
};

export default CheckBox;
